import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import _ from 'lodash';
import { b64utoutf8, KJUR } from 'jsrsasign';
import adminApi from 'src/api/auth/adminApi';
import authApi from 'src/api/auth/authApi';
import productApi, { ProductDto } from 'src/api/auth/productApi';
import {
  MENU_PREFIX_FXM_UI,
  MENU_PREFIX_FXM_USER_UI,
  userAllowedMenus,
  userDisabledRequest,
} from 'src/shared/utils/menuUtils';

import { RootState } from 'src/store';
import { setToken, removeToken } from 'src/shared/utils/sessionStorageManager';
import userRuleApi from 'src/api/user/userRuleApi';
import settingApi from 'src/api/settingApi';
import { toEntity } from '../user/userRule';

// # types
export interface SignInReqEntity {
  userId: string;
  password: string;
  redirectUrl: string;
}
export interface SignInWithTempTokenReqEntity {
  redirectUrl: string;
  token: string;
}

export interface TokenObject {
  user_name: string;
  request_ip: string;
  scope: string[];
  authorities: string[];
  exp: number;
  jti: string;
  client_id: string;
  need_password_change: boolean;
  password_change_alert: boolean | null;
  managed_dept_codes: string[];
  user_info: {
    userId: string;
    userName: string;
    userEmail: string;
    deptCode: string;
    deptName: string;
    positionCode: string;
    positionName: string;
    roleCode: string;
    roleName: string;
  };
}
interface TokenEntity {
  requestIp: string;
  scope: string[];
  authorities: string[];
  exp: number;
  jti: string;
  clientId: string;
  needPasswordChange: boolean;
  passwordChangeAlert: boolean | null;
  managedDeptCodes: string[];
  userInfo: {
    userId: string;
    userName: string;
    userEmail: string;
    deptCode: string;
    deptName: string;
    positionCode: string;
    positionName: string;
    roleCode: string;
    roleName: string;
  };
}
function toTokenEntity(tokenObject: TokenObject): TokenEntity {
  return {
    requestIp: tokenObject.request_ip,
    scope: tokenObject.scope,
    authorities: tokenObject.authorities,
    exp: tokenObject.exp,
    jti: tokenObject.jti,
    clientId: tokenObject.client_id,
    needPasswordChange: tokenObject.need_password_change,
    passwordChangeAlert: tokenObject.password_change_alert,
    managedDeptCodes: tokenObject.managed_dept_codes || [],
    userInfo: tokenObject.user_info,
  };
}

type ProductEntity = ProductDto;

export interface AuthEntity {
  tokenEntity: TokenEntity;
  tokenStr: string;
  menus: string[];
  disabledMenus: string[];
  productEntities: ProductEntity[];
}

interface AuthState {
  progressSignOut: boolean;
  authEntity: AuthEntity | undefined;
}

// # initial state
const initialState: AuthState = {
  progressSignOut: false,
  authEntity: undefined,
};

export const fxmuserMenus = [
  'fxmuserui-approval-pending', //
  'fxmuserui-approval-complete', //
];

// # thunks
async function createAuthEntity(token: string): Promise<AuthEntity> {
  const tokenDto = await authApi.getTokenKey();
  let tokenEntity: TokenEntity;

  try {
    const isValid = KJUR.jws.JWS.verifyJWT(token, tokenDto.value, { alg: ['RS256'] });
    if (!isValid) {
      throw new Error('verifyJWT failed');
    }
    const payloadObj = KJUR.jws.JWS.readSafeJSONString(
      b64utoutf8(token.split('.')[1])
    ) as TokenObject;

    tokenEntity = toTokenEntity(payloadObj);
  } catch (e) {
    console.error('[jsrsasign] jwt token error ', e);
    throw new Error('[T]token error occurred'); // [TODO]: resource
  }

  let adminMenus: string[] = [];
  let menus: string[] = [];
  let disabledMenus: string[] = [];
  let productEntities: ProductEntity[] = [];

  if (import.meta.env.VITE_APP_TYPE !== 'user') {
    try {
      const adminDto = await adminApi.assignmentsWithToken(token, tokenEntity.userInfo.userId);
      adminMenus = adminDto.type.menus;
    } catch (e) {
      console.log(e);
    }
    const allMenus = _.cloneDeep(adminMenus);
    menus = allMenus.filter(
      (menu) => menu.indexOf(MENU_PREFIX_FXM_UI) >= 0 || menu.indexOf(MENU_PREFIX_FXM_USER_UI) >= 0 // TODO MENU_PREFIX_COMMON_UI 제거
    );
    // if (menus.length === 0) {
    //   return Promise.reject('(미작업) 접근 권한이 없습니다.');
    // }

    const usingProductSet = allMenus
      .map((menu) => menu.split('-')[0])
      .filter((prefix) => prefix)
      .reduce((acc, prefix) => acc.add(prefix), new Set<string>())
      .add('fxmui');

    const productDtos = await productApi.products();

    const productEntitiesList = productDtos.products.map((dto) => ({
      ...dto,
      type: 'PRODUCT',
      enabled: usingProductSet.has(dto.context) && dto.enabled,
    }));
    const commonEntitiesList = productDtos.commons.map((dto) => ({
      ...dto,
      type: 'COMMON',
      enabled: usingProductSet.has(dto.context) && dto.enabled,
    }));

    productEntities = [...productEntitiesList, ...commonEntitiesList];
  }

  if (import.meta.env.VITE_APP_TYPE === 'user') {
    let userMenus: string[] = fxmuserMenus;
    const userRule = await userRuleApi.getRuleWithToken(token);

    const commonSetting = await settingApi.listCommonWithToken(token);
    const isVaildApprover = await userRuleApi.checkApproverValid(token);
    if (!isVaildApprover) {
      console.log('checkApproverValid', isVaildApprover);
      throw new Error('NO_APPROVER');
    }
    if (
      userRule !== undefined &&
      userRule !== null &&
      commonSetting !== undefined &&
      commonSetting !== null
    ) {
      const useProxy =
        commonSetting
          .filter((setting) => setting.name === 'PROXY')
          .filter((proxySetting) => proxySetting.globalSetting.useYn === 'N').length > 0
          ? false
          : true;
      userMenus = userAllowedMenus(toEntity(userRule), useProxy);
      disabledMenus = userDisabledRequest(toEntity(userRule));
      menus = menus.concat(userMenus);
    }
  } else if (import.meta.env.VITE_APP_TYPE === 'manager') {
    const subSetting = await settingApi.listSubWithToken(token);

    let managerMenus: string[] = [...menus];

    subSetting.forEach((policy) => {
      if (policy.name === 'DECRYPT' && policy.globalSubSetting.useYn === 'N') {
        managerMenus = managerMenus.filter((menu) => menu !== 'fxmui-policy-decryption');
      } else if (policy.name === 'PROVISIONAL' && policy.globalSubSetting.useYn === 'N') {
        managerMenus = managerMenus.filter((menu) => menu !== 'fxmui-policy-temporary-authority');
      } else if (policy.name === 'SCREEN' && policy.globalSubSetting.useYn === 'N') {
        managerMenus = managerMenus.filter((menu) => menu !== 'fxmui-policy-screen');
      } else if (policy.name === 'TAKEOUT' && policy.globalSubSetting.useYn === 'N') {
        managerMenus = managerMenus.filter((menu) => menu !== 'fxmui-policy-pc-checkout');
      } else if (policy.name === 'PRINTPOLICY' && policy.globalSubSetting.useYn === 'N') {
        managerMenus = managerMenus.filter((menu) => menu !== 'fxmui-policy-print-policy');
      } else if (policy.name === 'PRINTFILE' && policy.globalSubSetting.useYn === 'N') {
        managerMenus = managerMenus.filter((menu) => menu !== 'fxmui-policy-print-file');
      } else if (policy.name === 'PRINTCLIENT' && policy.globalSubSetting.useYn === 'N') {
        managerMenus = managerMenus.filter((menu) => menu !== 'fxmui-policy-print-client');
      }
    });

    if (
      !managerMenus.includes('fxmui-policy-decryption') &&
      !managerMenus.includes('fxmui-policy-temporary-authority') &&
      !managerMenus.includes('fxmui-policy-print-file') &&
      !managerMenus.includes('fxmui-policy-print-client')
    ) {
      managerMenus = managerMenus.filter((menu) => menu !== 'fxmui-statistics-document');
    }

    menus = [...managerMenus];
  } else if (import.meta.env.VITE_APP_TYPE === undefined) {
    menus = menus.concat(fxmuserMenus);
  }

  const authEntity: AuthEntity = {
    tokenEntity,
    tokenStr: token,
    disabledMenus,
    menus,
    productEntities,
  };
  return authEntity;
}
export const thunkSignIn = createAsyncThunk(
  'auth/signIn',
  async ({ userId, password, redirectUrl }: SignInReqEntity) => {
    const { accessToken } = await authApi.logIn(userId, password, redirectUrl);
    return createAuthEntity(accessToken);
  }
);
export const thunkSignInWithTempToken = createAsyncThunk(
  'auth/signInWithTempToken',
  async ({ token, redirectUrl }: SignInWithTempTokenReqEntity) => {
    const { accessToken } = await authApi.logInWithTempToken(redirectUrl, token);
    return createAuthEntity(accessToken);
  }
);
export const thunkSignInWithToken = createAsyncThunk(
  'auth/signInWithToken',
  async (token: string) => {
    return createAuthEntity(token);
  }
);
export const thunkSignOut = createAsyncThunk<
  void,
  void,
  {
    state: RootState;
  }
>('auth/signOut', async (arg, thunkApi) => {
  const state = thunkApi.getState();
  const userId = state.auth.authEntity?.tokenEntity.userInfo.userId;
  await authApi.logOut(userId);
});
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    startSignOut: (state) => {
      state.progressSignOut = true;
    },
    finishSignOut: (state) => {
      state.progressSignOut = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(thunkSignIn.fulfilled, (state, { payload: authEntity }) => {
      setToken(authEntity.tokenStr);
      state.authEntity = authEntity;
    });
    builder.addCase(thunkSignInWithTempToken.fulfilled, (state, { payload: authEntity }) => {
      setToken(authEntity.tokenStr);
      state.authEntity = authEntity;
    });
    builder.addCase(thunkSignInWithToken.fulfilled, (state, { payload: authEntity }) => {
      setToken(authEntity.tokenStr);
      state.authEntity = authEntity;
    });
    builder.addCase(thunkSignInWithToken.rejected, (state, { payload: authEntity }) => {
      removeToken();
      state.authEntity = undefined;
    });
    builder.addCase(thunkSignOut.fulfilled, (state, { payload: authEntity }) => {
      removeToken();
      state.authEntity = undefined;
    });
    builder.addCase(thunkSignOut.rejected, (state, { payload: authEntity }) => {
      removeToken();
      state.authEntity = undefined;
    });
  },
});

// # selectors
export const selectAuthEntity = (state: RootState): AuthEntity | undefined => {
  return state.auth.authEntity;
};
export const selectIsAuthenticated = (state: RootState): boolean => {
  return state.auth.authEntity !== undefined;
};
export const selectIsProgressSignOut = (state: RootState): boolean => {
  return state.auth.progressSignOut;
};

export const { startSignOut, finishSignOut } = authSlice.actions;
export default authSlice.reducer;
